/**
 * Weights that fit in the box they are printed in.
 *
 * A cable plant deals in everything from a few kilos of filler to a hundred
 * tonnes of copper, and "127,450 kg" in a summary card on a laptop screen runs
 * off the edge of it. Past a tonne the kilogram stops being the useful unit
 * anyway — nobody plans a run by the kilo at that scale.
 *
 * Precision falls as the number grows, which is how people read weights: 1.25 t
 * is a real distinction, 127.45 t is not, and the trailing digits are noise
 * that costs the width the number needed in the first place.
 *
 * The exact figure is never lost — `exactMass` gives it for a title attribute,
 * so hovering still tells you the kilogram.
 */

const TONNE = 1000;

const num = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** "850 kg", "1.25 t", "127 t" — whichever reads best at that size. */
export function formatMass(kg, { unit = true } = {}) {
  const v = num(kg);
  const abs = Math.abs(v);

  if (abs < TONNE) {
    // Under a tonne the kilogram is the working unit; a decimal only earns its
    // place while the number is small enough for it to mean something.
    const dp = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
    return `${v.toLocaleString(undefined, {
      minimumFractionDigits: dp,
      maximumFractionDigits: dp
    })}${unit ? ' kg' : ''}`;
  }

  const t = v / TONNE;
  const at = Math.abs(t);
  const dp = at >= 100 ? 0 : at >= 10 ? 1 : 2;
  return `${t.toLocaleString(undefined, {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp
  })}${unit ? ' t' : ''}`;
}

/** The full figure, for a tooltip — rounding is for the eye, not the record. */
export function exactMass(kg) {
  return `${num(kg).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  })} kg`;
}

/** Just the unit, when a layout needs the number and the unit apart. */
export function massUnit(kg) {
  return Math.abs(num(kg)) < TONNE ? 'kg' : 't';
}
